
/*
 * Copyright (c) 2014 - 2024 Comparatio USA LLC.
 */

import {Component, ElementRef, OnDestroy, OnInit, ViewChild, Inject} from '@angular/core';
import {Page} from 'app/model/page';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/do';
import 'rxjs/add/operator/delay';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute, ActivatedRouteSnapshot, Router} from '@angular/router';
import {permissions} from 'app/permissions';
import {ConfirmationService} from 'primeng/api';
import {TableExtensionService} from 'app/services/table-extension.service';
import {SortService} from 'app/services/sort.service';
import {AuthService} from 'app/auth/auth.service';
import {Title} from '@angular/platform-browser';
import {Subscription} from 'rxjs/Subscription';
import {ITicketsReplies} from './interfaces/ITicketsReplies';
import {ITicketsRepliesDates} from './interfaces/ITicketsRepliesDates';
import tableCols from './tickets-replies.cols';
import {DatesService} from 'app/services/dates.service';
import {TicketsRepliesService} from 'app/services/tickets-replies.service';
import {HttpClientModule} from '@angular/common/http';
import * as moment from 'moment';
import { Location } from '@angular/common';

@Component ( {
    providers: [
        TicketsRepliesService,
        AuthService,
        TableExtensionService,
        SortService,
        DatesService,
        HttpClientModule
    ],
    selector: 'app-tickets-replies',
    templateUrl: './tickets-replies.component.html',
    styleUrls: ['./tickets-replies.component.scss'],
})
export class TicketsRepliesComponent implements OnInit, OnDestroy {
    @ViewChild ( 'dataView' ) dataView: ElementRef;
    @ViewChild ( 'actionbar' ) actionbar: ElementRef;
    @ViewChild ( 'content' ) content: ElementRef;
    @ViewChild ( 'tableHeader' ) tableHeader: ElementRef;
    @ViewChild ( 'paginator' ) paginator: ElementRef;
    @ViewChild('tableFilter') tableFilter: ElementRef;
    public loading = false;
    public exportLoading = false;
    public page = new Page();
    public cols = [];
    public rows = [];
    public selected: any = null;
    public totalRecords = 0;
    public filtersApplied = false;
    public scrollHeight = '50px';
    private viewportHeight = 0;
    private viewportHeightListener: boolean;
    private subscriptions: Array<Subscription>;
    public size = 50;
    public userActionLoading = false;
    public sortApplied: boolean;
    public sortField: string;
    public sortOrder: number;
    public rowSelection: number;
    public record: ITicketsReplies;
    public dates: ITicketsRepliesDates;
    public errors: Array<any> = [];
    public action = '';
    public permissions = permissions;
    public hideTooltip = false;
    public viewFilters = true;
    public resultsFiltered = false;
    public resultsFilteredByDate = false;
    public reportRange: any;
    public initDate1: string;
    public initDate2: string;

    constructor (
        private ticketsRepliesService: TicketsRepliesService,
        private toastr: ToastrService,
        private sortService: SortService,
        private router: Router,
        private route: ActivatedRoute,
        private confirmationService: ConfirmationService,
        private authService: AuthService,
        private title: Title,
        private tableExtensionService: TableExtensionService,
        private location: Location
    ) {
        this.page.pageNumber = 0;
        this.page.size = 50;
        this.title.setTitle ( 'Teamwork Ticket & Reply Count' );
    }

    public ngOnInit () {
        this.loading = true;
        this.filtersApplied = false;
        this.sortApplied = false;
        this.sortField = '';
        this.sortOrder = 0;

        this.viewportHeightListener = true;

        const filterParams = this.route.snapshot.queryParams;
        this.parseParams(filterParams);

        this.subscriptions = [];
        this.cols = tableCols;

        this.record = this.ticketsRepliesService.initialize();
        this.dates = this.ticketsRepliesService.initializeDates();
        this.setInitDates();
        this.filterResults({ offset: this.page.pageNumber });

        this.screenHeightListener();
        this.viewFilters = true;
    }

    public ngOnDestroy (): void {
        this.viewportHeightListener = false;
        for ( const element of this.subscriptions ) {
            if ( element ) {
                element.unsubscribe();
            }
        }
    }

    public parseParams(filterParams) {
        if (filterParams !== undefined && filterParams !== null) {
            let showFilters = false;
            let sortApplied = false;
            for (const key of Object.keys(filterParams)) {
                const value = filterParams[key];
                if (value && value.toString() !== '') {
                    if (key.toString() === 'page') {
                        this.page.pageNumber = Number(value);
                    } else if (key.toString() === 'perPage') {
                        this.page.size = Number(value);
                        this.size = Number(value);
                    } else if (
                        key.toString() === 'SORT_COLUMN' ||
                        key.toString() === 'SORT_DIRECTION'
                    ) {
                        this.page.filterParams[key] = value;
                        sortApplied = true;
                    } else {
                        this.page.filterParams[key] = value;
                        showFilters = true;
                    }
                }
            }
            this.viewFilters = showFilters;
            this.filtersApplied = showFilters;
            this.sortApplied = sortApplied;

            this.parseSort(filterParams);
        }
    }

    private parseSort(filterParams) {
        const sortDetails = this.sortService.parseSort(filterParams);
        this.sortField = sortDetails.sortField;
        this.sortOrder = sortDetails.sortOrder;
    }

    public getData(reloadWithParams = true) {
        this.setPage({ offset: this.page.pageNumber }, reloadWithParams);
    }

    public setPage(pageInfo, reloadWithParams = true) {
        const date1 = moment(this.dates.DATE1);
        const date2 = moment(this.dates.DATE2);
        this.page.filterParams['DATE1'] = moment(date1).format('YYYY-MM-DD HH:mm:ss');
        this.page.filterParams['DATE2'] = moment(date2).format('YYYY-MM-DD HH:mm:ss');

        this.resultsFilteredByDate = true;

        if (reloadWithParams) {
            this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
            this.page.filterParams['perPage'] = pageInfo.size
                ? pageInfo.size
                : this.size;
            this.router.routeReuseStrategy.shouldReuseRoute = function (
                future: ActivatedRouteSnapshot,
                curr: ActivatedRouteSnapshot
            ) {
                return false;
            };
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: this.page.filterParams,
            });
        } else {
            this.loading = true;
            this.page.pageNumber = pageInfo.offset;
            this.subscriptions.push (
                this.ticketsRepliesService.getDataList ( this.page ).subscribe ( result => {
                    this.rows = result.data;
                    this.totalRecords = result.count ? result.count : 0;

                    setTimeout ( () => {
                        this.scrollHeight = this.getScrollHeight() + 'px';
                    } );

                    this.loading = false;
                }, () => {
                    this.loading = false;
                    window.dispatchEvent ( new Event ( 'resize' ) );
                    this.toastr.error ( 'Loading error occurred', 'Service Error', {
                        timeOut : 3000,
                    } );
                } )
            );
        }
    }

    public onHover(e, index = null) {
        const aux = e.target.offsetWidth < e.target.scrollWidth;
        this.hideTooltip = !aux;
    }

    public buttonPress(e, col, value) {
        if (e.keyCode === 13) {
            this.filterChanged(col, value);
        }
    }

    public clearFilters() {
        this.page.filterParams = {};
        this.viewFilters = false;
        this.filtersApplied = false;
        this.page.pageNumber = 0;
        this.filterResults({ offset: this.page.pageNumber });
    }

    public filterChanged(col, value) {
        this.loading = true;
        this.page.filterParams[col] = value;
        this.filtersApplied = Object.keys(this.page.filterParams).length > 0;
        this.page.pageNumber = 0;
        this.filterResults({ offset: this.page.pageNumber });
    }

    public getContentDialogHeight(): number {
        return (
            window.innerHeight - (
                this.actionbar.nativeElement.offsetHeight +
                48
            ) - 77
        );
    }

    public getScrollHeight(): number {
        return this.getContentDialogHeight () - (
            this.tableHeader.nativeElement.offsetHeight
            + this.paginator.nativeElement.offsetHeight + 52 + (
                this.viewFilters ? 52 : 0
            )
        );
    }

    public onColumnResize ( event: any ) {
        this.tableExtensionService.onColumnResize ( this.cols, event );
    }

    public refreshClick() {
        this.dates.DATE1 = '';
        this.dates.DATE2 = '';
        this.resultsFilteredByDate = false;
        this.page.filterParams = {};
        this.viewFilters = false;
        this.filtersApplied = false;
        this.page.pageNumber = 0;
        this.filterResults({ offset: this.page.pageNumber });
    }

    public toggleFilterView() {
        this.viewFilters = !this.viewFilters;
        this.onWindowResize();
    }

    public onWindowResize() {
        setTimeout(() => {
            this.scrollHeight = this.getScrollHeight() + 'px';
            window.dispatchEvent(new Event('resize'));
        }, 150);
    }

    public screenHeightListener() {
        if (!this.viewportHeightListener) {
            return;
        }
        if (window.innerHeight !== this.viewportHeight) {
            this.viewportHeight = window.innerHeight;
            this.onWindowResize();
        }
        setTimeout(() => {
            this.screenHeightListener();
        }, 500);
    }

    public backClick () {
        this.loading = false;
        this.location.back();
    }

    public onPage(event) {
        this.page.size = event.rows;
        this.filterResults({
            offset: event.first / event.rows
        });
    }

    public onSort(col) {
        if (col.sort) {
            if (this.page.filterParams.hasOwnProperty('SORT_COLUMN')) {
                if (this.page.filterParams['SORT_COLUMN'] === col.field) {
                    if (this.page.filterParams['SORT_DIRECTION'] === 'ASC') {
                        this.page.filterParams['SORT_DIRECTION'] = 'DESC';
                    } else {
                        delete this.page.filterParams['SORT_DIRECTION'];
                        delete this.page.filterParams['SORT_COLUMN'];
                    }
                } else {
                    this.page.filterParams['SORT_COLUMN'] = col.field;
                    this.page.filterParams['SORT_DIRECTION'] = 'ASC';
                }
            } else {
                this.page.filterParams['SORT_COLUMN'] = col.field;
                this.page.filterParams['SORT_DIRECTION'] = 'ASC';
            }

            this.parseSort(this.page.filterParams);
            this.filterResults({ offset: this.page.pageNumber });
        }
    }

    public clearResultFilter() {
        this.dates.DATE1 = '';
        this.dates.DATE2 = '';
        this.resultsFilteredByDate = false;
        this.page.pageNumber = 0;
        this.filterResults({ offset: this.page.pageNumber });
    }

    public setInitDates() {
        const date1 = new Date();
        this.dates.DATE2 = '' + date1.getFullYear() +
            '-' +
            (date1.getMonth() + 1) +
            '-' +
            (date1.getDate()) +
            ' 23:59:59';

        this.dates.DATE1 = '' + date1.getFullYear() +
            '-' +
            (date1.getMonth() + 1) +
            '-' +
            '01 00:00:00';
    }

    public filterResults(pageInfo: { offset: any; size?: any; }) {
        this.loading = true;
        this.page.filterParams['page'] = pageInfo.offset ? pageInfo.offset : 0;
        this.page.filterParams['perPage'] = pageInfo.size ? pageInfo.size : this.size;

        const date1 = moment(this.dates.DATE1);
        const date2 = moment(this.dates.DATE2);
        this.page.filterParams['DATE1'] = moment(date1).format('YYYY-MM-DD HH:mm:ss');
        this.page.filterParams['DATE2'] = moment(date2).format('YYYY-MM-DD HH:mm:ss');

        this.resultsFilteredByDate = true;

        this.subscriptions.push(
            this.ticketsRepliesService.getDataList(this.page).subscribe(result => {
                this.rows = result.data;
                this.totalRecords = result.count;
                this.loading = false;
            }, () => {
                this.loading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('Loading error occurred', 'Service Error', {
                    timeOut: 3000,
                });
            })
        );
    }

    exportClick() {
        this.exportLoading = true;
        this.ticketsRepliesService.export(this.page).subscribe(
            (result: { success: any; }) => {
                if (result.success) {
                    setTimeout(() => {
                        // delay to discourage hitting export multiple times
                        this.exportLoading = false;
                    }, 5000);
                    this.toastr.info(
                        'Export is added to queue.' +
                        ' Please check the File Exports to download the file once it is ready!',
                        'Update',
                        {
                            timeOut: 6000,
                        }
                    );
                } else {
                    this.exportLoading = false;
                    this.toastr.error('Error exporting data.');
                }
            },
            () => {
                this.exportLoading = false;
                window.dispatchEvent(new Event('resize'));
                this.toastr.error('Request timed out', 'Service Error');
            }
        );
    }
}



// WEBPACK FOOTER //
// /home/ubuntu/edi/angular/app/views/teamwork-reports/tickets-replies/tickets-replies.component.ts